import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import BeatLoader from "react-spinners/BeatLoader";
import TablePagination from "@mui/material/TablePagination";
import { toast } from "react-toastify";
import {
  portalChangePin,
  portalGetMe,
  portalListSessions,
  portalLoginActivity,
  portalLogoutAll,
  portalRevokeSession,
  portalUpdateIpWhitelist,
  portalUpdateMe,
} from "../../services/merchant-portal";
import { clearPortalTokens } from "../../utils/merchant-portal-storage";
import { usePortalAuth } from "../../contexts/PortalAuthContext";
import "./portal.scss";

const TABS = ["profile", "security", "api", "sessions", "activity"];

const pinSchema = Yup.object({
  current_pin: Yup.string()
    .matches(/^[0-9]{5}$/, "5-digit PIN")
    .required("Required"),
  new_pin: Yup.string()
    .matches(/^[0-9]{5}$/, "5-digit PIN")
    .required("Required"),
  new_pin_confirm: Yup.string()
    .oneOf([Yup.ref("new_pin")], "PINs must match")
    .required("Required"),
});

const MAX_WHITELIST_IPS = 50;
const IPV4_RE =
  /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

const parseIps = (text) => {
  const errors = [];
  const seen = new Set();
  const ips = [];
  const raw = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (raw.length > MAX_WHITELIST_IPS) {
    return { ips: [], errors: [`Max ${MAX_WHITELIST_IPS} IPs`] };
  }
  for (const ip of raw) {
    if (ip !== "*" && !IPV4_RE.test(ip)) {
      errors.push(`Invalid IP: ${ip}`);
      continue;
    }
    if (!seen.has(ip)) {
      seen.add(ip);
      ips.push(ip);
    }
  }
  return { ips, errors };
};

const maskCredential = (value) => {
  if (!value) return "—";
  return "•".repeat(Math.min(String(value).length, 32));
};

const MerchantPortalSettings = () => {
  const navigate = useNavigate();
  const { markLoggedOut } = usePortalAuth();
  const [tab, setTab] = useState("profile");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(0);
  const [activityRows, setActivityRows] = useState(20);
  const [ipsText, setIpsText] = useState("");
  const [usesEnvFallback, setUsesEnvFallback] = useState(false);
  const [savingIps, setSavingIps] = useState(false);
  const [credentialsVisible, setCredentialsVisible] = useState(false);

  const loadProfile = () =>
    portalGetMe().then((res) => {
      if (res.status_code === 200) {
        setProfile(res.data);
        const ips = res.data?.api?.ip_whitelist || [];
        setIpsText(ips.join("\n"));
      }
      return res;
    });

  const loadSessions = () =>
    portalListSessions().then((res) => {
      if (res.status_code === 200) {
        setSessions(res.data || []);
      }
    });

  const loadActivity = () =>
    portalLoginActivity(activityPage + 1, activityRows).then((res) => {
      if (res.status_code === 200) {
        setActivity(res.data?.items || []);
        setActivityTotal(res.data?.total ?? 0);
      }
    });

  useEffect(() => {
    setLoading(true);
    loadProfile()
      .catch((err) => toast.error(`${err}`))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab === "sessions") loadSessions();
    if (tab === "activity") loadActivity();
    if (tab !== "api") {
      setCredentialsVisible(false);
    }
  }, [tab, activityPage, activityRows]);

  const saveProfile = async (values, { setSubmitting }) => {
    try {
      const res = await portalUpdateMe({
        first_name: values.first_name,
        last_name: values.last_name,
        language: values.language,
        address: values.address,
        city: values.city,
      });
      if (res.status_code === 200) {
        setProfile(res.data);
        toast.success("Profile updated");
      } else {
        toast.error(res.message || "Update failed");
      }
    } catch (err) {
      toast.error(`${err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const saveIps = async () => {
    const { ips, errors } = parseIps(ipsText);
    if (errors.length) {
      toast.error(errors.join(" "));
      return;
    }
    setSavingIps(true);
    try {
      const res = await portalUpdateIpWhitelist(ips);
      if (res.status_code === 200) {
        setUsesEnvFallback(Boolean(res.data?.uses_env_fallback));
        toast.success("IP whitelist updated");
        await loadProfile();
      } else {
        toast.error(res.message || "Update failed");
      }
    } catch (err) {
      toast.error(`${err}`);
    } finally {
      setSavingIps(false);
    }
  };

  const handleChangePin = async (values, { setSubmitting, resetForm }) => {
    try {
      const res = await portalChangePin(values);
      if (res.status_code === 200) {
        toast.success(res.message || "PIN updated — sign in again");
        clearPortalTokens();
        markLoggedOut();
        resetForm();
        navigate("/merchant-portal/login", { replace: true });
      } else {
        toast.error(res.message || "PIN update failed");
      }
    } catch (err) {
      toast.error(`${err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const revokeSession = (sid) => {
    portalRevokeSession(sid)
      .then((res) => {
        if (res.status_code === 200) {
          toast.success("Session revoked");
          loadSessions();
        } else {
          toast.error(res.message || "Could not revoke");
        }
      })
      .catch((err) => toast.error(`${err}`));
  };

  const logoutAll = () => {
    portalLogoutAll()
      .then((res) => {
        if (res.status_code === 200) {
          clearPortalTokens();
          markLoggedOut();
          toast.info("All sessions revoked");
          navigate("/merchant-portal/login", { replace: true });
        } else {
          toast.error(res.message || "Failed");
        }
      })
      .catch((err) => toast.error(`${err}`));
  };

  if (loading || !profile) {
    return (
      <div className="flex justify-center py-16">
        <BeatLoader color="#03C9D7" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">Settings</h1>
      <div className="portal-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            className={`portal-tab${tab === t ? " portal-tab--active" : ""}`}
            onClick={() => setTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === "profile" ? (
        <div className="portal-card portal-form max-w-xl">
          <Formik
            enableReinitialize
            initialValues={{
              first_name: profile.first_name || "",
              last_name: profile.last_name || "",
              language: profile.language || "",
              address: profile.address || "",
              city: profile.city || "",
            }}
            onSubmit={saveProfile}
          >
            {({ handleSubmit, handleChange, values, isSubmitting }) => (
              <form onSubmit={handleSubmit}>
                <div className="field">
                  <label>First name</label>
                  <input name="first_name" value={values.first_name} onChange={handleChange} />
                </div>
                <div className="field">
                  <label>Last name</label>
                  <input name="last_name" value={values.last_name} onChange={handleChange} />
                </div>
                <div className="field">
                  <label>Language</label>
                  <input name="language" value={values.language} onChange={handleChange} />
                </div>
                <div className="field">
                  <label>Address</label>
                  <input name="address" value={values.address} onChange={handleChange} />
                </div>
                <div className="field">
                  <label>City</label>
                  <input name="city" value={values.city} onChange={handleChange} />
                </div>
                <p className="portal-muted text-sm mb-4">
                  Email: {profile.email || "—"} · Phone: {profile.phone || "—"}
                </p>
                <button type="submit" className="portal-btn" disabled={isSubmitting}>
                  Save profile
                </button>
              </form>
            )}
          </Formik>
        </div>
      ) : null}

      {tab === "security" ? (
        <div className="portal-card portal-form max-w-md">
          <p className="portal-muted text-sm mb-4">
            Changing your PIN signs you out of all portal sessions.
          </p>
          <Formik
            initialValues={{
              current_pin: "",
              new_pin: "",
              new_pin_confirm: "",
            }}
            validationSchema={pinSchema}
            onSubmit={handleChangePin}
          >
            {({
              handleSubmit,
              handleChange,
              values,
              errors,
              touched,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                {["current_pin", "new_pin", "new_pin_confirm"].map((name) => (
                  <div className="field" key={name}>
                    <label htmlFor={name}>{name.replace(/_/g, " ")}</label>
                    <input
                      id={name}
                      name={name}
                      type="password"
                      inputMode="numeric"
                      maxLength={5}
                      value={values[name]}
                      onChange={handleChange}
                    />
                    {errors[name] && touched[name] ? (
                      <div className="error">{errors[name]}</div>
                    ) : null}
                  </div>
                ))}
                <button type="submit" className="portal-btn" disabled={isSubmitting}>
                  Update PIN
                </button>
              </form>
            )}
          </Formik>
        </div>
      ) : null}

      {tab === "api" ? (
        <div className="portal-card max-w-2xl space-y-4">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <h3 className="font-semibold mb-0">API credentials</h3>
              <button
                type="button"
                className="portal-btn portal-btn--secondary text-sm"
                onClick={() => setCredentialsVisible((v) => !v)}
                aria-pressed={credentialsVisible}
              >
                {credentialsVisible ? "Hide credentials" : "Show credentials"}
              </button>
            </div>
            <p className="text-sm">
              API user:{" "}
              <code>
                {credentialsVisible
                  ? profile.api?.api_user || "—"
                  : maskCredential(profile.api?.api_user)}
              </code>
            </p>
            <p className="text-sm break-all">
              API secret:{" "}
              <code>
                {credentialsVisible
                  ? profile.api?.api_secret || "—"
                  : maskCredential(profile.api?.api_secret)}
              </code>
            </p>
            <p className="text-sm break-all">
              API key (disbursement):{" "}
              <code>
                {credentialsVisible
                  ? profile.api?.disbursement_api_key ||
                    profile.api?.api_key ||
                    "—"
                  : maskCredential(
                      profile.api?.disbursement_api_key || profile.api?.api_key,
                    )}
              </code>
            </p>
            <p className="text-sm break-all">
              API key (payment collection):{" "}
              <code>
                {credentialsVisible
                  ? profile.api?.collection_api_key || "—"
                  : maskCredential(profile.api?.collection_api_key)}
              </code>
            </p>
          </div>
          <div className="portal-form">
            <h3 className="font-semibold mb-2">IP whitelist</h3>
            {usesEnvFallback ? (
              <p className="portal-muted text-sm mb-2">
                Empty list uses server environment fallback.
              </p>
            ) : null}
            <textarea
              rows={8}
              value={ipsText}
              onChange={(e) => setIpsText(e.target.value)}
              placeholder="One IPv4 per line, or * for any"
            />
            <button
              type="button"
              className="portal-btn mt-3"
              onClick={saveIps}
              disabled={savingIps}
            >
              {savingIps ? "Saving…" : "Save whitelist"}
            </button>
          </div>
        </div>
      ) : null}

      {tab === "sessions" ? (
        <div className="portal-card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold">Active sessions</h3>
            <button type="button" className="portal-btn portal-btn--secondary" onClick={logoutAll}>
              Sign out everywhere
            </button>
          </div>
          {sessions.length === 0 ? (
            <p className="portal-muted">No active sessions.</p>
          ) : (
            <ul className="space-y-3 text-sm">
              {sessions.map((s) => (
                <li
                  key={s.id}
                  className="flex flex-wrap justify-between gap-2 border-b border-gray-100 dark:border-gray-800 pb-2"
                >
                  <div>
                    <div>{s.ip || "Unknown IP"}</div>
                    <div className="portal-muted text-xs truncate max-w-md">
                      {s.user_agent}
                    </div>
                    <div className="portal-muted text-xs">
                      Last used: {s.last_used_at ? String(s.last_used_at) : "—"}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-red-500 text-sm"
                    onClick={() => revokeSession(s.id)}
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {tab === "activity" ? (
        <div className="portal-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left opacity-75 border-b">
                <th className="py-2 pr-3">Event</th>
                <th className="py-2 pr-3">Outcome</th>
                <th className="py-2 pr-3">IP</th>
                <th className="py-2 pr-3">When</th>
              </tr>
            </thead>
            <tbody>
              {activity.map((row) => (
                <tr key={row.id} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 pr-3">{row.event}</td>
                  <td className="py-2 pr-3">{row.outcome}</td>
                  <td className="py-2 pr-3">{row.ip || "—"}</td>
                  <td className="py-2 pr-3">{String(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            component="div"
            count={activityTotal}
            page={activityPage}
            onPageChange={(_, p) => setActivityPage(p)}
            rowsPerPage={activityRows}
            onRowsPerPageChange={(e) => {
              setActivityRows(parseInt(e.target.value, 10));
              setActivityPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </div>
      ) : null}
    </div>
  );
};

export default MerchantPortalSettings;
